import store from '../store/index'
import { endSession, startSession } from '../utils/session'

const token = () => `Bearer ${store.getters['app/TOKEN']()}`

const handleErrors = (resp) => {
  if (!resp.ok) {
    return resp.json()
      .catch(() => null)
      .then(json => {
        // 401 status indicates expired authorization token --> user must login again
        if (resp.status === 401) {
          endSession()
        }

        const error = new Error(`HTTP Error ${resp.statusText}`)
        error.status = resp.statusText
        error.statusCode = resp.status
        error.response = resp
        error.json = json
        throw error
      })
  }

  // session updated by server so refresh client side
  startSession()
  return resp
}

const json = (resp) => {
  return resp && resp.status !== 204 ? resp.json() : {}
}

export function fetchWithErrorHandling (url, init) {
  if (!init.headers) {
    init.headers = {}
  }

  if (!init.headers['Content-Type']) {
    init.headers['Content-Type'] = 'application/json'
  }

  return fetch(url, init)
    .then(handleErrors)
    .then(json)
}

let apiConfig = {}
export function getApiConfig () {
  return apiConfig
}

export function getDefaultHeaders () {
  return {
    'Content-Type': 'application/json',
    Authorization: token(),
  }
}

export default {
  install (Vue, options) {
    const { mainEndpoint, adminEndpoint, adpEndpoint } = options
    apiConfig = options

    if (!mainEndpoint || !adminEndpoint || !adpEndpoint) { console.error('CareConnect endpoint not defined') }

    startSession()

    Vue.prototype.$careConnect = {
      logIn: async (email, password) => {
        return fetchWithErrorHandling(`${mainEndpoint}login`, {
          method: 'POST',
          body: JSON.stringify({
            password,
            username: email,
            agencies: false,
            type: 'webapp',
          }),
        })
      },

      recover: async (email) => {
        return fetchWithErrorHandling(`${mainEndpoint}recover?${new URLSearchParams({ email, type: 'user', source: 'web' })}`, {
          method: 'GET',
        })
      },
      createAgency: async (data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies`, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      getAgency: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getSolutions: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}solutions`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getAdminGroups: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}users/groups`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      createAdminGroup: async (group) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/groups`, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(group),
        })
      },
      updateAdminGroup: async (groupId, group) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/groups/${groupId}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(group),
        })
      },
      deleteAdminGroup: async (groupId) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/groups/${groupId}`, {
          method: 'DELETE',
          headers: {
            Authorization: token(),
          },
        })
      },
      getUserRoles: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}users/roles`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getDisciplines: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/disciplines`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getServiceDisciplineMappingFromAMS: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/ams/sdmapping`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getOfficesFromAMS: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/ams/offices`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getRolesFromAMS: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/ams/roles`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getAgencies: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateAgency: async (agencyId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      startDailySync: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/ams/triggerDataReconsile`, {
          method: 'POST',
          headers: {
            Authorization: token(),
          },
        })
      },
      uploadAgencyLogo: async (uploadUrl, file) => {
        const upload = async event => {
          const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: event.currentTarget.result,
            headers: {
              'Content-Length': file.size,
              'Content-Type': file.type,
            },
          })

          if (response.status >= 300) {
            throw new Error(`HTTP error ${response.statusText}`)
          }

          return response
        }

        const reader = new FileReader()
        reader.onload = upload
        reader.readAsArrayBuffer(file)
      },
      getFileUploadPermissions: async (agencyId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/logo`, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      updateAgencyLogo: async (agencyId, fileUrl) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/logo`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fileUrl }),
        })
      },
      createAgencyMedicalIntegration: async (agencyId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/amis`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token(),
          },
          body: JSON.stringify(data),
        })
      },
      updateAgencyMedicalIntegration: async (agencyId, amisId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/amis/${amisId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token(),
          },
          body: JSON.stringify(data),
        })
      },
      getAgencyProcedureMappings: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/proceduremappings`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      upsertAgencyProcedureMappings: async (agencyId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/proceduremappings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token(),
          },
          body: JSON.stringify(data),
        })
      },
      deleteAgencyProcedureMapping: async (agencyId, mappingId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/proceduremappings/${mappingId}`, {
          method: 'DELETE',
          headers: {
            Authorization: token(),
          },
        })
      },
      fetchAgencyMobileHealthWebhookToken: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/mobilehealth/webhookToken`, {
          method: 'POST',
          headers: {
            Authorization: token(),
          },
        })
      },
      getEmployees: async (agencyId) => {
        return fetchWithErrorHandling(`${mainEndpoint}employees?agencyId=${agencyId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getEmployee: async (employeeId) => {
        return fetchWithErrorHandling(`${mainEndpoint}employees/${employeeId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      createEmployee: async (data) => {
        return fetchWithErrorHandling(`${mainEndpoint}employees`, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      getCaregiver: async (caregiverId) => {
        return fetchWithErrorHandling(`${mainEndpoint}caregivers/${caregiverId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateCaregiver: async (caregiverId, data) => {
        return fetchWithErrorHandling(`${mainEndpoint}caregivers/${caregiverId}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      getCaregiverByUUID: async (UUID) => {
        return fetchWithErrorHandling(`${adminEndpoint}caregivers/${UUID}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getCaregiverAgencyProfile: async (id, agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}caregivers/${id}/${agencyId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getEmployeeAgencyProfile: async (id, agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}employees/${id}/${agencyId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      searchEmployees: async ({ pageNum, filters }) => {
        const params = new URLSearchParams({
          pageNum,
          filters: JSON.stringify(filters),
        })

        return fetchWithErrorHandling(`${adminEndpoint}employees?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateCaregiverAgencyProfile: async (id, agencyId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}caregivers/${id}/${agencyId}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      updateEmployeeAgencyProfile: async (id, agencyId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}employees/${id}/${agencyId}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      getInternalAdmins: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}internaladmins`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      createInternalAdmin: async (data) => {
        return fetchWithErrorHandling(`${adminEndpoint}internaladmins`, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      getUserAgencyRoles: async (id) => {
        return fetchWithErrorHandling(`${adminEndpoint}internaladmins/${id}/roles`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateAdminRoles: async (id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}internaladmins/${id}/roles`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      updateEmployeeRoles: async (id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}employees/${id}/roles`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      updateUserProfile: async (userId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/${userId}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      searchCaregivers: async ({ pageNum, filters }) => {
        const params = new URLSearchParams({
          pageNum,
          filters: JSON.stringify(filters),
        })

        return fetchWithErrorHandling(`${adminEndpoint}caregivers?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      searchClients: async ({ pageNum, filters }) => {
        const params = new URLSearchParams({
          pageNum,
          filters: JSON.stringify(filters),
        })

        return fetchWithErrorHandling(`${adminEndpoint}clients?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getClientProfile: async (id, agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}clients/${id}/${agencyId}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      searchEventLogs: async ({ pageNum, filters }) => {
        const params = new URLSearchParams({
          nextPage: pageNum,
          filters: JSON.stringify(filters),
        })

        return fetchWithErrorHandling(`${adminEndpoint}event-logs?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateUser: async (id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}internaladmins/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      listReports: async (id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}reports`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateReport: async (id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}reports/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      deleteReport: async (id) => {
        return fetchWithErrorHandling(`${adminEndpoint}reports/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: token(),
          },
        })
      },
      createReport: async (data) => {
        return fetchWithErrorHandling(`${adminEndpoint}reports`, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      previewReport: async (id) => {
        return fetchWithErrorHandling(`${adminEndpoint}reports/${id}/preview`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getMaintenances: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}maintenance`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      upsertMaintenance: async (data) => {
        return fetchWithErrorHandling(`${adminEndpoint}maintenance`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      removeMaintenance: async (start) => {
        return fetchWithErrorHandling(`${adminEndpoint}maintenance/${start}`, {
          method: 'DELETE',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
        })
      },
      searchUsers: async ({ pageNum, filters }) => {
        const params = new URLSearchParams({
          pageNum,
          filters: JSON.stringify(filters),
        })

        return fetchWithErrorHandling(`${adminEndpoint}users?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      searchUserByEmail: async (email) => {
        return fetchWithErrorHandling(`${adminEndpoint}users?email=${encodeURIComponent(email)}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      searchUserById: async (userId) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/${userId.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      searchUserCaregivers: async (userId) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/${userId.toString()}/caregivers`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      updateUserById: async (data, id) => {
        return fetchWithErrorHandling(`${adminEndpoint}users/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      },
      linkUserToEmployee: async (employeeId, userId) => {
        return fetchWithErrorHandling(`${adminEndpoint}employees/${employeeId}/user`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        })
      },
      linkUserToCaregiver: async (id, userId, agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}caregivers/${id}/user`, {
          method: 'PUT',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, agencyId }),
        })
      },
      listMobileHealthResults: async (opts) => {
        return fetchWithErrorHandling(`${adminEndpoint}mobilehealth?${new URLSearchParams(opts)}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getSecondaryStatusMapping: async (id) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${id}/secondarymapping`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getAmsMapping: async (id) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${id}/secondarymapping/ams`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      removeAmsConfig: async (id) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${id}/ams/config`, {
          method: 'DELETE',
          headers: {
            Authorization: token(),
          },
        })
      },
      createSecondaryStatusMapping: async (id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${id}/secondarymapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token(),
          },
          body: JSON.stringify(data),
        })
      },
      updateSecondaryStatusMapping: async (id, mappingId, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${id}/secondarymapping/${mappingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token(),
          },
          body: JSON.stringify(data),
        })
      },
      deleteSecondaryStatusMapping: async (id, mappingId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${id}/secondarymapping/${mappingId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token(),
          },
        })
      },
      getGlobalConfig: async () => {
        return fetchWithErrorHandling(`${adminEndpoint}config`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getAgencyServices: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}demo/${agencyId}/services`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getComplianceBlocks: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/compliance/blocks`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      createMissingDocumentMappings: async (agencyId) => {
        return fetchWithErrorHandling(`${adminEndpoint}agencies/${agencyId}/documentmappings/missing`, {
          method: 'POST',
          headers: {
            Authorization: token(),
          },
        })
      },
      initProcedureMappingSuggestions: async (agencyId, dryRun = false) => {
        const targetEndpoint = `${adminEndpoint}agencies/${agencyId}/init-procedure-mapping-suggestions`
        return fetchWithErrorHandling(targetEndpoint, {
          method: 'POST',
          headers: {
            Authorization: token(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dryRun }),
        })
      },
      searchShifts: async ({ pageNum, filters }) => {
        const params = new URLSearchParams({
          pageNum,
          filters: JSON.stringify(filters),
        })

        return fetchWithErrorHandling(`${adminEndpoint}shifts?${params.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: token(),
          },
        })
      },
      getShiftAgency: async ({ shiftId, agencyId }) => {
        const params = new URLSearchParams({ agencyId })

        return fetchWithErrorHandling(`${adminEndpoint}shifts/${shiftId}/agency?${params.toString()}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },

      getShiftClient: async ({ shiftId, agencyId, clientId }) => {
        const params = new URLSearchParams({ agencyId, clientId })

        return fetchWithErrorHandling(`${adminEndpoint}shifts/${shiftId}/client?${params.toString()}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },

      getShiftCaregiver: async ({ shiftId, agencyId, caregiverId }) => {
        const params = new URLSearchParams({ agencyId, caregiverId })

        return fetchWithErrorHandling(`${adminEndpoint}shifts/${shiftId}/caregiver?${params.toString()}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },

      getShiftEventLogs: async ({ shiftId, agencyId }) => {
        const params = new URLSearchParams({ agencyId })

        return fetchWithErrorHandling(`${adminEndpoint}shifts/${shiftId}/event-logs?${params.toString()}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },

      // Entity Manager - Generic CRUD
      entityManagerList: async (entityName, { pageNum, pageSize, search, searchColumn }) => {
        const params = new URLSearchParams({ pageNum, pageSize })
        if (search) { params.set('search', search) }

        if (searchColumn) { params.set('searchColumn', searchColumn) }

        return fetchWithErrorHandling(`${adminEndpoint}entity-manager/${entityName}?${params.toString()}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },
      entityManagerGet: async (entityName, id) => {
        return fetchWithErrorHandling(`${adminEndpoint}entity-manager/${entityName}/${id}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },
      entityManagerCreate: async (entityName, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}entity-manager/${entityName}`, {
          method: 'POST',
          headers: { Authorization: token(), 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
      },
      entityManagerUpdate: async (entityName, id, data) => {
        return fetchWithErrorHandling(`${adminEndpoint}entity-manager/${entityName}/${id}`, {
          method: 'PUT',
          headers: { Authorization: token(), 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
      },
      entityManagerDelete: async (entityName, id) => {
        return fetchWithErrorHandling(`${adminEndpoint}entity-manager/${entityName}/${id}`, {
          method: 'DELETE',
          headers: { Authorization: token() },
        })
      },

      // LMS
      lmsDiscoverCourses: async (prefix) => {
        const params = new URLSearchParams()
        if (prefix) { params.set('prefix', prefix) }

        const qs = params.toString() ? `?${params.toString()}` : ''
        return fetchWithErrorHandling(`${adminEndpoint}lms/courses/discover${qs}`, {
          method: 'GET',
          headers: { Authorization: token() },
        })
      },
      lmsImportFromManifest: async (manifestCourseId) => {
        return fetchWithErrorHandling(`${adminEndpoint}lms/courses/import`, {
          method: 'POST',
          headers: { Authorization: token(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ manifestCourseId }),
        })
      },
      lmsResyncFromManifest: async (courseId) => {
        return fetchWithErrorHandling(`${adminEndpoint}lms/courses/${courseId}/resync`, {
          method: 'POST',
          headers: { Authorization: token() },
        })
      },
      createApiKey: async (data) => {
        return fetchWithErrorHandling(`${adminEndpoint}api-keys`, {
          method: 'POST',
          headers: { Authorization: token(), 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
      },
    }
  },
}

// const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))
